import React from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import { useSelector } from 'react-redux';
import { CreditCardOutlined } from '@ant-design/icons';

// DS
import { Col, Row, VertTypography, VertIcon, VertImage } from '@vertem-core/vertice-ds';

// STYLES
import 'Assets/styles/default/components/Orders/OrderDetails/OrderDetailsStyles.less';

// UTILS
import { getOrderStatus } from 'Utils/orders';
import { currencyMask } from 'Utils/commons/mask';
import { pointsMask } from 'Utils/commons/points';
import ynToBool from 'Utils/ynToBool';

// ORDER PAYMENT COMPONENT
const OrderPayment = ({ order }) => {
    /* Hooks */
    const Catalog = useSelector(({ Catalog: data }) => data);

    /* Vars */
    const statusColor = getOrderStatus(order.status).color;
    const statusLabel = getOrderStatus(order.status).label;
    const hasShippingValue = order.orderTotalShippingValue > 0;
    const isPaymentWithPointsAndCash = order.orderPaymentDetailMoneyAndPoints.purchaseValueDecimal !== 0;
    const { currencyConfiguration: { decimalPlaces, pointsName } } = Catalog;
    const formattedTotalItems = pointsMask(order.totalItems, decimalPlaces, pointsName);
    const hasFreeItems = formattedTotalItems.value == 0;
    const formattedTotalShipping = pointsMask(order.orderTotalShippingValue, decimalPlaces, pointsName);
    const totalDiscount = order?.orderPromoCodeValue || 0;
    let totalOrderFinal = order?.orderTotalValue;
    const totalPointsAndCash = order?.orderPaymentDetailMoneyAndPoints?.pointsUsedDecimal;
    const formattedPoinsCashOrder = pointsMask(totalPointsAndCash, decimalPlaces, pointsName);
    const formattedTotalOrder = pointsMask(totalOrderFinal,decimalPlaces,pointsName);
    const hasFreeOrder = formattedTotalOrder.value == 0;
    const formattedTotalDiscount = pointsMask(totalDiscount, decimalPlaces, pointsName);



    /* Renders */
    return (
        <Col className='col'>
            {/* Desktop */}
            <MediaQuery minWidth={769}>
                <VertTypography.Paragraph className='payment-title'>
                    <VertIcon component={CreditCardOutlined} />
                    Pagamento
                </VertTypography.Paragraph>

                <Row className='payment-wrapper' align='middle' gutter={6}>
                    <Col className='payment-details col-3'>
                        <VertTypography.Paragraph className='payment-info'>
                            {isPaymentWithPointsAndCash
                                ? 'Pagamento no cartão'
                                : 'Pagamento em pontos'
                            }
                        </VertTypography.Paragraph>
                        {isPaymentWithPointsAndCash && (
                            <>
                                <Row align='middle' gutter={6}>
                                    <VertImage
                                        rootClassName='card-brand-image'
                                        alt={order.orderPaymentDetailMoneyAndPoints.brandName}
                                        src={order.orderPaymentDetailMoneyAndPoints.imageUrlCardBrand}
                                    />
                                    <VertTypography.Paragraph className='payment-info'>
                                        <span>
                                            {order.orderPaymentDetailMoneyAndPoints.numberParcelsDecimal} {' '}
                                            parcela(s) de {currencyMask(order.orderPaymentDetailMoneyAndPoints.valueParcelDecimal)}
                                        </span>
                                    </VertTypography.Paragraph>
                                </Row>
                                <VertTypography.Paragraph className='payment-info'>
                                    <span>
                                        {order.orderPaymentDetailMoneyAndPoints.brandName} {' '}
                                        **** {order.orderPaymentDetailMoneyAndPoints.lastCreditCardDigits}
                                    </span>
                                </VertTypography.Paragraph>
                            </>
                        )}
                        <VertTypography.Paragraph className={`payment-info ${statusColor}`}>
                            {statusLabel}
                        </VertTypography.Paragraph>
                    </Col>
                    <Col className='payment-summary col'>
                        <VertTypography.Paragraph className='payment-info'>
                            <span>Resumo do pedido</span>
                        </VertTypography.Paragraph>
                        <VertTypography.Paragraph className="payment-info">
                            <span>Total dos itens:</span> <span className={`${hasFreeItems ? "color-highlight" : ""}`}>{hasFreeItems ? "Grátis" : `${formattedTotalItems.value} ${formattedTotalItems.label}`}</span>
                        </VertTypography.Paragraph>

                      
                        <VertTypography.Paragraph className='payment-info'
                              style={{
                                display: ynToBool(process.env.REACT_APP_HIDE_FREIGHT) ? 'none' : ''
                              }}
                        >
                            <span>Frete: {' '}</span>
                            {hasShippingValue && <span>{`${formattedTotalShipping.value} ${formattedTotalShipping.label}`}</span>}
                            {!hasShippingValue && <span className='highlight'>Grátis</span>}
                        </VertTypography.Paragraph>
                        {totalDiscount > 0 &&(
                            <VertTypography.Paragraph className='payment-info'>
                                <span>Desconto:</span> <span>{`${formattedTotalDiscount.value} ${formattedTotalDiscount.label}`}</span>
                            </VertTypography.Paragraph>
                        )}
                        <VertTypography.Paragraph className='payment-info'>
                            <span>Total do resgate:</span> {' '}
                            <span className={`${hasFreeOrder ? "highlight" : ""}`}>{hasFreeOrder ? "Grátis" : `${formattedTotalOrder.value} ${formattedTotalOrder.label}`}</span> {' '}
                        </VertTypography.Paragraph>
                        {
                            order?.orderPaymentDetailMoneyAndPoints?.pointsUsedDecimal > 0 &&< VertTypography.Paragraph className='payment-info'>
                            <span>Total do pagamento:</span> {' '}
                            <span>{formattedPoinsCashOrder.value} {formattedPoinsCashOrder.label}</span> {' '}
                            {isPaymentWithPointsAndCash && (
                                <span>+ {currencyMask(order.orderPaymentDetailMoneyAndPoints.purchaseValueDecimal)}</span>
                            )}
                        </VertTypography.Paragraph>
                        }
                    </Col>
                </Row>
            </MediaQuery>

            {/* Mobile */}
            <MediaQuery maxWidth={768}>
                <VertTypography.Paragraph className='payment-title'>
                    <VertIcon component={CreditCardOutlined} />
                    Pagamento
                </VertTypography.Paragraph>
                <Col className='payment-wrapper'>
                    <Col className='payment-details col'>
                        <VertTypography.Paragraph className='payment-info'>
                            {isPaymentWithPointsAndCash
                                ? 'Pagamento no cartão de crédito'
                                : 'Pagamento em pontos'
                            }
                        </VertTypography.Paragraph>
                        {isPaymentWithPointsAndCash && (
                            <>
                                <Row align='middle' gutter={6}>
                                    <VertImage
                                        rootClassName='card-brand-image'
                                        alt={order.orderPaymentDetailMoneyAndPoints.brandName}
                                        src={order.orderPaymentDetailMoneyAndPoints.imageUrlCardBrand}
                                    />
                                    <VertTypography.Paragraph className='payment-info'>
                                        <span>
                                        {order.orderPaymentDetailMoneyAndPoints.numberParcelsDecimal} {' '}
                                        parcela(s) de {currencyMask(order.orderPaymentDetailMoneyAndPoints.valueParcelDecimal)}
                                        </span>
                                    </VertTypography.Paragraph>
                                </Row>
                                <VertTypography.Paragraph className='payment-info'>
                                    <span>
                                        {order.orderPaymentDetailMoneyAndPoints.brandName} {' '}
                                        **** {order.orderPaymentDetailMoneyAndPoints.lastCreditCardDigits}
                                    </span>
                                </VertTypography.Paragraph>
                            </>
                        )}
                        <VertTypography.Paragraph className={`payment-info ${statusColor}`}>
                            {statusLabel}
                        </VertTypography.Paragraph>
                    </Col>
                    <Col className='divider' />
                    <Col className='payment-summary col'>
                        <VertTypography.Paragraph className='payment-info'>
                            <span>Resumo do pedido</span>
                        </VertTypography.Paragraph>
                        <VertTypography.Paragraph className='payment-info'>
                            <span>Total dos itens:</span> <span className={`${hasFreeItems ? "color-highlight" : ""}`}>{hasFreeItems ? "Grátis" : `${formattedTotalItems.value} ${formattedTotalItems.label}`}</span>
                        </VertTypography.Paragraph>
                        <VertTypography.Paragraph className='payment-info'
                              style={{
                                display: ynToBool(process.env.REACT_APP_HIDE_FREIGHT) ? 'none' : ''
                              }}
                        >
                            <span>Frete: {' '}</span>
                            {hasShippingValue && <span>{`${formattedTotalShipping.value} ${formattedTotalShipping.label}`}</span>}
                            {!hasShippingValue && <span className='highlight'>Grátis</span>}
                        </VertTypography.Paragraph>
                        {totalDiscount > 0 &&(
                            <VertTypography.Paragraph className='payment-info'>
                                <span>Desconto:</span> <span>{`${formattedTotalDiscount.value} ${formattedTotalDiscount.label}`}</span>
                            </VertTypography.Paragraph>
                        )}
                        <VertTypography.Paragraph className='payment-info'>
                            <span>Total do resgate:</span> {' '}
                            <span className={`${hasFreeOrder ? "highlight" : ""}`}>{hasFreeOrder ? "Grátis" : `${formattedTotalOrder.value} ${formattedTotalOrder.label}`}</span> {' '}
                        </VertTypography.Paragraph>
                        {
                            order?.orderPaymentDetailMoneyAndPoints?.pointsUsedDecimal > 0 &&< VertTypography.Paragraph className='payment-info'>
                            <span>Total do pagamento:</span> {' '}
                            <span>{formattedPoinsCashOrder.value} {formattedPoinsCashOrder.label}</span> {' '}
                            {isPaymentWithPointsAndCash && (
                                <span>+ {currencyMask(order.orderPaymentDetailMoneyAndPoints.purchaseValueDecimal)}</span>
                            )}
                        </VertTypography.Paragraph>
                        }
                       
                    </Col>
                </Col>
            </MediaQuery>
        </Col>
    );
}

OrderPayment.propTypes = {
    order: PropTypes.array.isRequired,
};

export default OrderPayment;
